console.log('cart-rules.js: File loaded successfully.');

/**
 * Validates the cart against rules for free gifts and makes necessary adjustments.
 * @returns {Promise<object>} - A promise that resolves with the (potentially updated) cart object.
 */
async function validateCartRules() {
  console.log('cart-rules.js: validateCartRules called');

  let cart;
  try {
    console.log('cart-rules.js: Fetching full cart state from /cart.js...');
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`HTTP error when fetching /cart.js! status: ${response.status}`);
    }
    cart = await response.json();
    console.log('cart-rules.js: Successfully fetched full cart state:', JSON.parse(JSON.stringify(cart)));
  } catch (error) {
    console.error('cart-rules.js: Failed to fetch full cart state from /cart.js. Aborting validation.', error);
    return null;
  }

  // Identify all free gifts in the cart
  const freeGifts = cart.items.filter(item => 
    item.properties && item.properties._free_gift === true
  );
  
  if (freeGifts.length === 0) {
    console.log('cart-rules.js: No free gifts in cart to validate.');
    return cart;
  }
  
  console.log('cart-rules.js: Found free gifts in cart:', freeGifts.length);
  
  // DEBUG: Log all cart items to inspect them
  console.log('cart-rules.js: All cart items:', cart.items.map(item => ({
    title: item.title,
    handle: item.handle,
    variant_id: item.variant_id,
    id: item.id,
    product_id: item.product_id,
    properties: item.properties || 'none'
  })));
  
  let updated = false;
  const updates = {}; // For a single /cart/update.js call
  
  // Get product data for all special products to check their variants
  const specialProductsFromWindow = typeof window !== 'undefined' ? window.specialProducts : null;
  
  if (!specialProductsFromWindow || !Array.isArray(specialProductsFromWindow)) {
    console.log('cart-rules.js: window.specialProducts not found or not an array');
    return cart;
  }
  
  // DEBUG: Log window.specialProducts
  console.log('cart-rules.js: window.specialProducts data:', specialProductsFromWindow);
  
  try {
    // Gather data about all special products and their variants
    const specialProductsData = await Promise.all(
      specialProductsFromWindow.map(special => 
        // Use product.js instead of products.json for more reliable data
        fetch(`/products/${special.product}.js`)
          .then(response => {
            if (!response.ok) {
              console.error(`cart-rules.js: Error fetching product ${special.product}: ${response.status}`);
              return { json: async () => null };
            }
            return response.json();
          })
          .then(productData => {
            if (!productData) {
              console.error(`cart-rules.js: No product data returned for ${special.product}`);
              return {
                special,
                variants: [],
                productId: null
              };
            }
            
            // DEBUG: Log the raw product data
            console.log(`cart-rules.js: Product data for ${special.product}:`, {
              id: productData.id,
              handle: productData.handle,
              variant_count: productData.variants ? productData.variants.length : 0
            });
            
            return {
              special,
              variants: productData.variants ? productData.variants.map(v => v.id.toString()) : [],
              productId: productData.id ? productData.id.toString() : null
            };
          })
          .catch(error => {
            console.error(`cart-rules.js: Failed to fetch product data for ${special.product}:`, error);
            return {
              special,
              variants: [],
              productId: null
            };
          })
      )
    );
    
    // DEBUG: Log the special products data with variants
    specialProductsData.forEach(spd => {
      console.log(`cart-rules.js: Special product ${spd.special.product} variants:`, spd.variants);
    });
    
    // Check each free gift to see if its associated product exists in the cart
    for (const freeGift of freeGifts) {
      // Get which special product this free gift is associated with
      const associatedProduct = freeGift.properties._associated_product;
      if (!associatedProduct) {
        console.log(`cart-rules.js: Found orphaned free gift with no associated product. Marking for removal.`);
        updates[freeGift.key] = 0;
        updated = true;
        continue;
      }
      
      console.log(`cart-rules.js: Processing free gift associated with ${associatedProduct}`);
      
      // Find the special product data for this associated product
      const specialProductData = specialProductsData.find(spd => 
        spd.special.product === associatedProduct
      );
      
      if (!specialProductData || !specialProductData.variants || specialProductData.variants.length === 0) {
        console.log(`cart-rules.js: Cannot find special product data for ${associatedProduct}. Marking free gift for removal.`);
        updates[freeGift.key] = 0;
        updated = true;
        continue;
      }
      
      // DEBUG: Log the specific special product data we're using
      console.log(`cart-rules.js: Found special product data for ${associatedProduct}:`, {
        product: specialProductData.special.product,
        variants: specialProductData.variants,
        productId: specialProductData.productId
      });
      
      // Check if any variant of this special product exists in cart
      let mainProductInCart = false;
      console.log(`cart-rules.js: Checking if any ${associatedProduct} variant exists in cart...`);
      
      for (const item of cart.items) {
        // Try checking against variant IDs
        const itemVariantId = item.variant_id.toString();
        
        // Also check product ID in case the variant comparison fails
        const itemProductId = item.product_id ? item.product_id.toString() : '';
        const specialProductId = specialProductData.productId;
        
        const matchesVariant = specialProductData.variants.includes(itemVariantId);
        const matchesProduct = specialProductId && specialProductId === itemProductId;
        
        // DEBUG: Log each item check in detail
        console.log(`cart-rules.js: Checking cart item: "${item.title}" (variant ID: ${itemVariantId}, product ID: ${itemProductId})`);
        console.log(`cart-rules.js: Against ${associatedProduct} (product ID: ${specialProductId})`);
        console.log(`cart-rules.js: Variant match: ${matchesVariant}, Product match: ${matchesProduct}`);
        
        if (matchesVariant || matchesProduct) {
          console.log(`cart-rules.js: ✓ MATCH FOUND - Item "${item.title}" is a variant of ${associatedProduct}`);
          mainProductInCart = true;
          break;
        }
      }
      
      // If the main product is not in cart, this free gift is orphaned
      if (!mainProductInCart) {
        console.log(`cart-rules.js: Main product ${associatedProduct} not in cart. Marking free gift for removal.`);
        updates[freeGift.key] = 0;
        updated = true;
      } else {
        console.log(`cart-rules.js: Main product ${associatedProduct} is in cart. Free gift is valid.`);
      }
    }
    
    // If any updates were made, send them to Shopify
    if (updated && Object.keys(updates).length > 0) {
      console.log('cart-rules.js: Applying cart updates:', JSON.parse(JSON.stringify(updates)));
      try {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates })
        });
        
        if (!updateResponse.ok) {
          throw new Error(`HTTP error when updating cart! status: ${updateResponse.status}`);
        }
        
        const updatedCart = await updateResponse.json();
        console.log('cart-rules.js: Cart updated successfully');
        
        // Dispatch cart update event
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('cart:update', { 
            detail: { source: 'cart-rules' } 
          }));
        }
        
        return updatedCart;
      } catch (error) {
        console.error('cart-rules.js: Error updating cart:', error);
        return cart;
      }
    } else {
      console.log('cart-rules.js: No updates needed');
      return cart;
    }
  } catch (error) {
    console.error('cart-rules.js: Error during validation:', error);
    return cart;
  }
}

// Check if we're in a browser environment
if (typeof window !== 'undefined') {
  // Make function available globally
  window.validateCartRules = validateCartRules;
} 